import type { BlendMode, BlendIfConfig } from "../document/Layer";
import type { LayerStyles } from "../document/LayerStyle";
import type { Document } from "../document/Document";
import type { Layer } from "../document/Layer";
import type { PathModel } from "../document/PathModel";
import type { AntiAliasMode, WarpTextParams } from "../document/TypeLayer";
import type { Command } from "./Command";

interface Patchable {
  visible: boolean;
  opacity: number;
  fillOpacity: number;
  blendMode: BlendMode;
  name: string;
  text: string;
  fontFamily: string;
  fontSize: number;
  x: number;
  y: number;
  lockTransparency: boolean;
  lockImage: boolean;
  lockPosition: boolean;
  lockAll: boolean;
  clippedToBelow: boolean;
  styles?: LayerStyles;
  params?: Record<string, number | string | boolean>;
  blendIf?: BlendIfConfig;
  vectorMask?: PathModel | null;
  path?: PathModel;
  colorLabel?: import("../document/Layer").LayerColorLabel;
  linkedLayerIds?: readonly string[];
  fauxBold?: boolean;
  fauxItalic?: boolean;
  allCaps?: boolean;
  smallCaps?: boolean;
  superscript?: boolean;
  subscript?: boolean;
  antiAlias?: AntiAliasMode;
  leading?: number;
  tracking?: number;
  baselineShift?: number;
  underline?: boolean;
  strikethrough?: boolean;
  fontWeight?: number;
  fontStyle?: "normal" | "italic";
  textAlign?: "left" | "center" | "right" | "justify";
  textBaseline?: "top" | "middle" | "bottom";
  color?: { r: number; g: number; b: number; a: number };
  embedded?: Document;
  // i14: Warp Text (Layer > Type > Warp Text… + the Type options bar's Warp
  // button) patches this to apply/undo a warp on a TypeLayer.
  warp?: WarpTextParams;
}

export class SetLayerPropertyCommand implements Command {
  readonly kind = "set-layer-property";
  constructor(
    private readonly layerId: string,
    private readonly patch: Partial<Patchable>,
  ) {}

  apply(doc: Document): Document {
    return { ...doc, layers: mapLayers(doc.layers, this.layerId, this.patch) };
  }

  invert(before: Document, _after: Document): Command {
    const original = findLayer(before.layers, this.layerId);
    if (!original) return this;
    const inverse: Partial<Patchable> = {};
    if ("visible" in this.patch) inverse.visible = original.visible;
    if ("opacity" in this.patch) inverse.opacity = original.opacity;
    if ("fillOpacity" in this.patch) inverse.fillOpacity = (original as any).fillOpacity ?? 1;
    if ("blendMode" in this.patch) inverse.blendMode = original.blendMode;
    if ("name" in this.patch) inverse.name = original.name;
    if ("text" in this.patch) inverse.text = (original as any).text;
    if ("x" in this.patch) inverse.x = (original as any).x ?? 0;
    if ("y" in this.patch) inverse.y = (original as any).y ?? 0;
    if ("lockTransparency" in this.patch) inverse.lockTransparency = !!(original as any).lockTransparency;
    if ("lockImage" in this.patch) inverse.lockImage = !!(original as any).lockImage;
    if ("lockPosition" in this.patch) inverse.lockPosition = !!(original as any).lockPosition;
    if ("lockAll" in this.patch) inverse.lockAll = !!(original as any).lockAll;
    if ("clippedToBelow" in this.patch) inverse.clippedToBelow = !!(original as any).clippedToBelow;
    if ("styles" in this.patch) inverse.styles = (original as any).styles;
    if ("params" in this.patch) inverse.params = { ...((original as any).params ?? {}) };
    if ("blendIf" in this.patch) inverse.blendIf = (original as any).blendIf;
    if ("warp" in this.patch) inverse.warp = (original as any).warp;
    return new SetLayerPropertyCommand(this.layerId, inverse);
  }
}

function mapLayers(layers: readonly Layer[], id: string, patch: Partial<Patchable>): Layer[] {
  return layers.map((l) => {
    if (l.id === id) return { ...l, ...patch };
    if (l.kind === "group") return { ...l, children: mapLayers(l.children, id, patch) };
    return l;
  });
}

function findLayer(layers: readonly Layer[], id: string): Layer | null {
  for (const l of layers) {
    if (l.id === id) return l;
    if (l.kind === "group") {
      const inner = findLayer(l.children, id);
      if (inner) return inner;
    }
  }
  return null;
}
